import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Edit, Trash2 } from "react-feather";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Table } from "antd";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import SupplierModal from "../../core/modals/peoples/supplierModal";
import { useDispatch, useSelector } from "react-redux";
import { setToogleHeader, setSupplierData, setCurrentSupplier } from "../../core/redux/action";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ChevronUp, PlusCircle } from "feather-icons-react/build/IconComponents";
import { useToast } from "../../Toaster/ToastContext";
import { apiClient } from "../../services/apiClient";

const Suppliers = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const supplierData = useSelector((state) => state.supplierdata);
  const rawCurrentSupplier = useSelector(
    (state) => state.currentSupplier,
    (prev, next) => prev?.id === next?.id
  );
  const currentSupplier = useMemo(() => rawCurrentSupplier, [rawCurrentSupplier?.id]);
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const hasFetched = useRef(false);
  const isDeleting = useRef(false); 
  const operationResult = useRef(null);
  const MySwal = withReactContent(Swal);
  const [pagination, setPagination] = useState({
    currentPage: 1, 
    pageSize: 10,
    totalPages: 0,
    totalCount: 0,
  });

  const fetchSuppliers = useCallback(async (currentPage = 1, pageSize = 10) => {
    if (hasFetched.current || isDeleting.current) return;
    setLoading(true);
    try {
      const result = await apiClient.post("/paginate-supplier", {
        pageIndex: currentPage - 1,
        pageSize,
        previousPageIndex: 0,
      });
      if (result.success) {
        const transformedData = result.data.map((supplier) => ({
          ...supplier,
          supplierName: supplier.supplier_name,
          image: supplier.avatar,
        }));
        setSuppliers(transformedData);
        setFilteredSuppliers(transformedData);
        dispatch(setSupplierData(transformedData));
        setPagination({
          currentPage: result.currentPage || currentPage,
          pageSize: result.pageSize || pageSize,
          totalPages: result.totalPages || 1,
          totalCount: result.totalCount || 0,
        });
      } else {
        showToast(result.message || "Failed to fetch suppliers", "error", "Suppliers");
      }
    } catch (error) {
      showToast(error.message || "Failed to fetch suppliers", "error", "Suppliers");
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  }, [showToast, dispatch]);

  useEffect(() => {
    fetchSuppliers(pagination.currentPage, pagination.pageSize);
  }, [fetchSuppliers, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    if (supplierData && !isDeleting.current) {
      setSuppliers(supplierData);
      setFilteredSuppliers(supplierData);
    }
  }, [supplierData]);

  const handleModalClose = useCallback(() => {
    if (operationResult.current?.success) {
      hasFetched.current = false; 
      fetchSuppliers(pagination.currentPage, pagination.pageSize);
    }
    operationResult.current = null; 
  }, [fetchSuppliers, pagination.currentPage, pagination.pageSize]);

  const handleOperationComplete = useCallback((result) => {
    operationResult.current = result; 
  }, []);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    const filtered = suppliers.filter(
      (item) =>
        item.supplier_name.toLowerCase().includes(term) ||
        item.email.toLowerCase().includes(term) ||
        item.phone.toLowerCase().includes(term) ||
        item.country.toLowerCase().includes(term)
    );
    setFilteredSuppliers(filtered);
  };

  const handleAddSupplier = () => {
    dispatch(setCurrentSupplier(null));
  };

  const handleEditSupplier = useCallback((supplier) => {
    dispatch(setCurrentSupplier({ ...supplier, image: supplier.avatar }));
  }, [dispatch]);

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  const showConfirmationAlert = useCallback(async (record) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Yes, delete it!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          isDeleting.current = true;
          const data = await apiClient.post("/delete-supplier", {
            id: record.id,
          });
          if (data.success) {
            showToast(data.msg || "Supplier deleted successfully", "success", "Suppliers");
            hasFetched.current = false;
            await fetchSuppliers(pagination.currentPage, pagination.pageSize);
          } else {
            showToast(data.msg || "Failed to delete supplier", "error", "Suppliers");
          }
        } catch (error) {
          showToast(error.message || "Failed to delete supplier", "error", "Suppliers");
        } finally {
          setLoading(false);
          isDeleting.current = false;
        }
      }
    });
  }, [fetchSuppliers, showToast, MySwal, pagination.currentPage, pagination.pageSize]);

  const columns = useMemo(() => [
    {
      title: "Supplier Name",
      dataIndex: "supplierName",
      render: (text, record) => (
        <span className="productimgname">
          <Link to="#" className="product-img stock-img">
            <ImageWithBasePath alt="Supplier Image" src={record.avatar || ""} />
          </Link>
          <Link to="#">{record.supplier_name}</Link>
        </span>
      ),
      sorter: (a, b) => a.supplier_name.length - b.supplier_name.length,
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a, b) => a.email.length - b.email.length,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      sorter: (a, b) => a.phone.length - b.phone.length,
    },
    {
      title: "Country",
      dataIndex: "country",
      sorter: (a, b) => a.country.length - b.country.length,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, record) => (
        <td className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#supplier-modal"
              onClick={() => handleEditSupplier(record)}
            >
              <Edit className="feather-edit" />
            </Link>
            <Link
              className="confirm-text p-2"
              to="#"
              onClick={() => showConfirmationAlert(record)}
            >
              <Trash2 className="feather-trash-2" />
            </Link>
          </div>
        </td>
      ),
    },
  ], [handleEditSupplier, showConfirmationAlert]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Supplier List</h4>
              <h6>Manage Your Supplier</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => {
                    dispatch(setToogleHeader(!data));
                  }}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
          <div className="page-btn">
            <Link
              to="#"
              className="btn btn-added"
              onClick={handleAddSupplier}
              data-bs-toggle="modal"
              data-bs-target="#supplier-modal"
            >
              <PlusCircle className="me-2" />
              Add New Supplier
            </Link>
          </div>
        </div>
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search by Name, Email, Phone, or Country"
                    className="form-control form-control-sm formsearch"
                    onChange={handleSearch}
                  />
                  <Link to="#" className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <Table
                className="table datanew"
                columns={columns}
                dataSource={filteredSuppliers}
                rowKey={(record) => record.id}
                loading={loading}
                pagination={{
                  current: pagination.currentPage,
                  pageSize: pagination.pageSize,
                  total: pagination.totalCount,
                  onChange: async (page, pageSize) => {
                    setPagination((prev) => ({
                      ...prev,
                      currentPage: page,
                      pageSize: pageSize,
                    }));
                    hasFetched.current = false;
                    await fetchSuppliers(page, pageSize);
                  },
                  showSizeChanger: true,
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <SupplierModal
        supplier={currentSupplier}
        onOperationComplete={handleOperationComplete}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default Suppliers;